import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluationService, OperationEvaluation, EvaluationCriteria } from '@/services/evaluationService';
import EvaluationAnalytics from './EvaluationAnalytics';
import api from '@/services/api';
import { AlertTriangle } from 'lucide-react';

// Categorías y Criterios basados en el estándar OPC de VANTAK
// Los textos visibles (name/desc) se resuelven vía i18n usando id/code como clave.
const EVALUATION_SCHEMA = [
    {
        id: 'PLANNING',
        weight: 20,
        icon: '🗺️',
        criteria: [
            { code: 'PLAN_01' },
            { code: 'PLAN_02' },
            { code: 'PLAN_03' }
        ]
    },
    {
        id: 'ROLES',
        weight: 15,
        icon: '👥',
        criteria: [
            { code: 'ROLES_01' },
            { code: 'ROLES_02' },
            { code: 'ROLES_03' }
        ]
    },
    {
        id: 'POLICE_COORD',
        weight: 15,
        icon: '👮',
        criteria: [
            { code: 'POLICE_01' },
            { code: 'POLICE_02' }
        ]
    },
    {
        id: 'INST_COORD',
        weight: 10,
        icon: '🏛️',
        criteria: [
            { code: 'INST_01' },
            { code: 'INST_02' }
        ]
    },
    {
        id: 'INFORMATION',
        weight: 15,
        icon: '📡',
        criteria: [
            { code: 'INFO_01' },
            { code: 'INFO_02' },
            { code: 'INFO_03' }
        ]
    },
    {
        id: 'LOGISTICS',
        weight: 10,
        icon: '🚛',
        criteria: [
            { code: 'LOG_01' },
            { code: 'LOG_02' }
        ]
    },
    {
        id: 'PROTOCOLS',
        weight: 10,
        icon: '📋',
        criteria: [
            { code: 'PROT_01' },
            { code: 'PROT_02' }
        ]
    },
    {
        id: 'OBJECTIVES',
        weight: 5,
        icon: '🎯',
        criteria: [
            { code: 'OBJ_01' },
            { code: 'OBJ_02' }
        ]
    },
    {
        id: 'CRITICAL_FINDINGS',
        weight: 0,
        icon: '⚠️',
        criteria: [] // Este paso usará campos de texto especiales
    }
];

export default function EvaluationPage() {
    const { t } = useTranslation();
    const [evaluations, setEvaluations] = useState<OperationEvaluation[]>([]);
    const [operations, setOperations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvalForModal, setSelectedEvalForModal] = useState<OperationEvaluation | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [activeTab, setActiveTab] = useState<'list' | 'analytics'>('list');

    // Wizard State
    const [currentStep, setCurrentStep] = useState(0);
    const [newEval, setNewEval] = useState<Partial<OperationEvaluation>>({
        operationId: 0,
        status: 'DRAFT',
        criteria: [],
        generalObservations: '',
        recommendations: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            console.log('Fetching evaluations and operations...');
            const [evalsRes, opsRes] = await Promise.all([
                evaluationService.getAll(),
                api.get('/api/v1/operations?size=100')
            ]);

            console.log('Evaluations response:', evalsRes);

            // Robust evaluation data extraction
            let evsData = [];
            if (Array.isArray(evalsRes)) {
                evsData = evalsRes;
            } else if (evalsRes && Array.isArray(evalsRes.content)) {
                evsData = evalsRes.content;
            } else if (evalsRes && Array.isArray(evalsRes.data)) {
                evsData = evalsRes.data;
            }
            setEvaluations(evsData);

            // Robust operations data extraction
            let opsData = [];
            if (Array.isArray(opsRes.data)) {
                opsData = opsRes.data;
            } else if (opsRes.data && Array.isArray(opsRes.data.content)) {
                opsData = opsRes.data.content;
            } else if (opsRes.data && Array.isArray(opsRes.data.results)) {
                opsData = opsRes.data.results;
            }

            console.log('Processed operations:', opsData);
            setOperations(opsData);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRating = (stepIndex: number, criterionCode: string, rating: number) => {
        const category = EVALUATION_SCHEMA[stepIndex];
        const criterion = category.criteria.find(c => c.code === criterionCode);

        const ratingLabels = ['DEFICIENTE', 'ADECUADO', 'OPTIMO'];

        const newCriterion: EvaluationCriteria = {
            category: t(`evaluation.schema.${category.id}.name`),
            criterionCode: criterionCode,
            criterionName: t(`evaluation.criteria.${criterion!.code}.name`),
            rating: rating,
            ratingLabel: ratingLabels[rating],
            observations: ''
        };

        const existingCriteria = [...(newEval.criteria || [])];
        const index = existingCriteria.findIndex(c => c.criterionCode === criterionCode);

        if (index >= 0) {
            existingCriteria[index] = newCriterion;
        } else {
            existingCriteria.push(newCriterion);
        }

        setNewEval({ ...newEval, criteria: existingCriteria });
    };

    const calculateCurrentScore = () => {
        if (!newEval.criteria) return 0;
        let total = 0;

        EVALUATION_SCHEMA.forEach(cat => {
            const catCriteria = cat.criteria.map(c => c.code);
            const scores = newEval.criteria?.filter(c => catCriteria.includes(c.criterionCode)) || [];

            if (scores.length > 0) {
                // Score de la categoría: promedio de puntuación (0-1) escalado a 100
                const catScore = (scores.reduce((acc, c) => acc + (c.rating / 2), 0) / cat.criteria.length) * 100;
                total += (catScore * cat.weight / 100);
            }
        });

        return total;
    };

    const handleSubmit = async () => {
        try {
            if (!newEval.operationId) return alert(t('evaluation.alertSelectOperation'));
            await evaluationService.create(newEval as OperationEvaluation);
            setIsCreating(false);
            setCurrentStep(0);
            setNewEval({ operationId: 0, criteria: [], status: 'DRAFT' });
            loadData();
        } catch (error) {
            console.error(error);
            alert(t('evaluation.alertSaveError'));
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto min-h-screen bg-[#0F172A] text-white">
            {/* Header */}
            <div className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">
                        {t('evaluation.title')}
                    </h1>
                    <p className="text-slate-400 mt-2">{t('evaluation.subtitle')}</p>
                </div>
                {!isCreating && (
                    <div className="flex gap-4">
                        <div className="bg-slate-800/80 p-1.5 rounded-2xl border border-slate-700 flex gap-2">
                            <button
                                onClick={() => setActiveTab('list')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'list' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                            >
                                {t('evaluation.tabList')}
                            </button>
                            <button
                                onClick={() => setActiveTab('analytics')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'analytics' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                            >
                                {t('evaluation.tabAnalytics')}
                            </button>
                        </div>
                        <button
                            onClick={() => setIsCreating(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('evaluation.newEvaluation')}
                        </button>
                    </div>
                )}
            </div>

            {isCreating ? (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {/* ... Wizard content ... */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-3xl overflow-hidden backdrop-blur-md mb-6">
                        <div className="h-1 bg-slate-700 w-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-500 transition-all duration-500 ease-out"
                                style={{ width: `${(currentStep / EVALUATION_SCHEMA.length) * 100}%` }}
                            ></div>
                        </div>

                        <div className="p-8">
                            {currentStep === 0 ? (
                                <div className="max-w-2xl mx-auto py-10">
                                    <h2 className="text-2xl font-bold mb-6 text-center">{t('evaluation.selectOperationTitle')}</h2>
                                    <select
                                        className="w-full bg-slate-900 border border-slate-700 p-4 rounded-xl text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                                        value={newEval.operationId}
                                        onChange={(e) => setNewEval({ ...newEval, operationId: parseInt(e.target.value) })}
                                    >
                                        <option value="0">{t('evaluation.selectOperationPlaceholder')}</option>
                                        {operations.map(op => (
                                            <option key={op.id} value={op.id}>{op.code} - {op.description || op.name}</option>
                                        ))}
                                    </select>
                                    <p className="mt-4 text-slate-400 text-sm italic">{t('evaluation.selectOperationHint')}</p>
                                </div>
                            ) : EVALUATION_SCHEMA[currentStep - 1].id === 'CRITICAL_FINDINGS' ? (
                                <div className="max-w-4xl mx-auto py-6">
                                    <div className="bg-amber-500/10 border border-amber-500/30 p-8 rounded-3xl mb-8">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-amber-500 p-2 rounded-lg">
                                                <AlertTriangle className="w-6 h-6 text-slate-900" />
                                            </div>
                                            <h3 className="text-xl font-bold text-amber-100">{t('evaluation.findingsReportTitle')}</h3>
                                        </div>
                                        <p className="text-amber-200/70 text-sm mb-6 leading-relaxed">
                                            {t('evaluation.findingsReportIntro')}
                                        </p>

                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-bold text-slate-300 mb-2 uppercase tracking-wider">{t('evaluation.incidentDetailLabel')}</label>
                                                <textarea
                                                    className="w-full bg-slate-900/80 border border-slate-700 rounded-2xl p-4 text-white min-h-[150px] focus:ring-2 focus:ring-amber-500/50 outline-none transition-all"
                                                    placeholder={t('evaluation.incidentDetailPlaceholder')}
                                                    value={newEval.generalObservations}
                                                    onChange={(e) => setNewEval({ ...newEval, generalObservations: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-slate-300 mb-2 uppercase tracking-wider">{t('evaluation.strategicRecommendationLabel')}</label>
                                                <textarea
                                                    className="w-full bg-slate-900/80 border border-slate-700 rounded-2xl p-4 text-white min-h-[100px] focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                                                    placeholder={t('evaluation.strategicRecommendationPlaceholder')}
                                                    value={newEval.recommendations}
                                                    onChange={(e) => setNewEval({ ...newEval, recommendations: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                                    {/* ... Sidebar Stats ... */}
                                    <div className="lg:col-span-3 border-r border-slate-700/50 pr-8">
                                        <div className="sticky top-0">
                                            <div className="text-center mb-8">
                                                <div className="text-5xl font-bold text-indigo-400 mb-2">{calculateCurrentScore().toFixed(1)}</div>
                                                <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">{t('evaluation.projectedScore')}</div>
                                            </div>

                                            <div className="space-y-3">
                                                {EVALUATION_SCHEMA.map((step, idx) => (
                                                    <div key={step.id} className={`flex items-center gap-3 p-2 rounded-lg transition-all ${currentStep === idx + 1 ? 'bg-indigo-500/10 text-indigo-400' : 'text-slate-500 opacity-60'}`}>
                                                        <span className="text-lg">{step.icon}</span>
                                                        <span className="text-xs font-bold whitespace-nowrap overflow-hidden text-ellipsis">{t(`evaluation.schema.${step.id}.name`)}</span>
                                                        {idx + 1 < currentStep && <svg className="w-4 h-4 ml-auto text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Criteria Form */}
                                    <div className="lg:col-span-9">
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="text-4xl">{EVALUATION_SCHEMA[currentStep - 1].icon}</div>
                                            <div>
                                                <h2 className="text-2xl font-bold">{t(`evaluation.schema.${EVALUATION_SCHEMA[currentStep - 1].id}.name`)}</h2>
                                                <p className="text-slate-400">{t('evaluation.weightInEvaluation', { weight: EVALUATION_SCHEMA[currentStep - 1].weight })}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-8">
                                            {EVALUATION_SCHEMA[currentStep - 1].criteria.map((criterion) => (
                                                <div key={criterion.code} className="bg-slate-900/50 p-6 rounded-2xl border border-slate-700/50 hover:border-slate-600 transition-all">
                                                    <div className="mb-4">
                                                        <h3 className="text-lg font-bold text-slate-100">{t(`evaluation.criteria.${criterion.code}.name`)}</h3>
                                                        <p className="text-sm text-slate-400 mt-1">{t(`evaluation.criteria.${criterion.code}.desc`)}</p>
                                                    </div>

                                                    <div className="grid grid-cols-3 gap-4">
                                                        {[
                                                            { val: 0, label: t('evaluation.ratingDeficient'), color: 'hover:bg-red-500/20 border-red-500/30' },
                                                            { val: 1, label: t('evaluation.ratingAdequate'), color: 'hover:bg-blue-500/20 border-blue-500/30' },
                                                            { val: 2, label: t('evaluation.ratingOptimal'), color: 'hover:bg-emerald-500/20 border-emerald-500/30' }
                                                        ].map((btn) => (
                                                            <button
                                                                key={btn.val}
                                                                onClick={() => handleRating(currentStep - 1, criterion.code, btn.val)}
                                                                className={`p-4 rounded-xl border text-sm font-bold transition-all ${newEval.criteria?.find(c => c.criterionCode === criterion.code)?.rating === btn.val
                                                                    ? (btn.val === 0 ? 'bg-red-500 border-red-500 text-white' : btn.val === 1 ? 'bg-blue-500 border-blue-500 text-white' : 'bg-emerald-500 border-emerald-500 text-white')
                                                                    : `bg-slate-800 border-slate-700 text-slate-500 ${btn.color}`
                                                                    }`}
                                                            >
                                                                {btn.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Buttons */}
                        <div className="bg-slate-900/80 p-6 border-t border-slate-700 flex justify-between items-center">
                            <button
                                onClick={() => currentStep === 0 ? setIsCreating(false) : setCurrentStep(prev => prev - 1)}
                                className="px-6 py-2 rounded-xl text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                {currentStep === 0 ? t('evaluation.cancel') : t('evaluation.back')}
                            </button>

                            <div className="flex items-center gap-4">
                                {currentStep > 0 && (
                                    <div className="text-sm text-slate-500 pr-4 border-r border-slate-700">
                                        {t('evaluation.stepOf', { current: currentStep, total: EVALUATION_SCHEMA.length })}
                                    </div>
                                )}
                                <button
                                    onClick={() => currentStep < EVALUATION_SCHEMA.length ? setCurrentStep(prev => prev + 1) : handleSubmit()}
                                    disabled={currentStep === 0 && newEval.operationId === 0}
                                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2"
                                >
                                    {currentStep === EVALUATION_SCHEMA.length ? t('evaluation.finishEvaluation') : t('evaluation.continue')}
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : activeTab === 'analytics' ? (
                <EvaluationAnalytics />
            ) : (
                <>
                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="text-slate-400 text-sm font-medium mb-1">{t('evaluation.statTotal')}</div>
                            <div className="text-3xl font-bold">{evaluations.length}</div>
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="text-slate-400 text-sm font-medium mb-1">{t('evaluation.statLevelI')}</div>
                            <div className="text-3xl font-bold text-emerald-400">
                                {evaluations.filter(e => e.opcLevel === 'NIVEL_I').length}
                            </div>
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="text-slate-400 text-sm font-medium mb-1">{t('evaluation.statLevelII')}</div>
                            <div className="text-3xl font-bold text-blue-400">
                                {evaluations.filter(e => e.opcLevel === 'NIVEL_II').length}
                            </div>
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="text-slate-400 text-sm font-medium mb-1">{t('evaluation.statLevelIII')}</div>
                            <div className="text-3xl font-bold text-amber-500">
                                {evaluations.filter(e => e.opcLevel === 'NIVEL_III').length}
                            </div>
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                            <div className="text-slate-400 text-sm font-medium mb-1">{t('evaluation.statAvgScore')}</div>
                            <div className="text-3xl font-bold text-indigo-400">
                                {evaluations.length > 0
                                    ? (evaluations.reduce((acc, curr) => acc + (curr.totalScore || 0), 0) / evaluations.length).toFixed(1)
                                    : '0.0'}
                            </div>
                        </div>
                    </div>

                    {/* List */}
                    <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl overflow-hidden backdrop-blur-sm">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="px-6 py-4 text-slate-400 font-medium">{t('evaluation.colOperation')}</th>
                                    <th className="px-6 py-4 text-slate-400 font-medium">{t('evaluation.colClassification')}</th>
                                    <th className="px-6 py-4 text-slate-400 font-medium text-center">{t('evaluation.colScore')}</th>
                                    <th className="px-6 py-4 text-slate-400 font-medium">{t('evaluation.colStatus')}</th>
                                    <th className="px-6 py-4 text-slate-400 font-medium text-right">{t('evaluation.colActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-20 text-center">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
                                        </td>
                                    </tr>
                                ) : evaluations.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-20 text-center text-slate-500">
                                            {t('evaluation.emptyList')}
                                        </td>
                                    </tr>
                                ) : (
                                    evaluations.map((evaluation) => {
                                        console.log('Rendering evaluation:', evaluation);
                                        return (
                                            <tr key={evaluation.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-indigo-400">{t('evaluation.opLabel', { id: evaluation.operationId })}</div>
                                                    <div className="text-xs text-slate-500">{t('evaluation.dateLabel', { date: evaluation.evaluationDate ? new Date(evaluation.evaluationDate).toLocaleDateString() : t('evaluation.notAvailable') })}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${evaluation.opcLevel === 'NIVEL_I' ? 'bg-emerald-500/20 text-emerald-400' :
                                                        evaluation.opcLevel === 'NIVEL_II' ? 'bg-blue-500/20 text-blue-400' :
                                                            'bg-amber-500/20 text-amber-400'
                                                        }`}>
                                                        {evaluation.opcLevel || t('evaluation.pending')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="text-lg font-bold">{(evaluation.totalScore || 0).toFixed(1)}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${evaluation.status === 'APPROVED' ? 'bg-emerald-500' : 'bg-slate-500'
                                                            }`}></div>
                                                        <span className="text-sm">{evaluation.status}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        {(evaluation.generalObservations || evaluation.recommendations) && (
                                                            <button
                                                                onClick={() => setSelectedEvalForModal(evaluation)}
                                                                className="p-2 hover:bg-amber-500/20 rounded-lg text-amber-500 transition-all flex items-center gap-2"
                                                                title={t('evaluation.viewCriticalFindings')}
                                                            >
                                                                <AlertTriangle className="w-5 h-5" />
                                                                {t('evaluation.finding')}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => evaluationService.downloadPdf(evaluation.id!)}
                                                            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-all flex items-center gap-2"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            PDF
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Modal de Hallazgos y Recomendaciones Premium */}
            {selectedEvalForModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setSelectedEvalForModal(null)}></div>
                    <div className="relative bg-slate-900 border border-slate-700 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Header */}
                        <div className="bg-amber-500/10 border-b border-amber-500/20 p-6 flex justify-between items-center">
                            <div className="flex items-center gap-3 text-amber-500">
                                <AlertTriangle className="w-6 h-6" />
                                <h3 className="text-xl font-bold uppercase tracking-tight">{t('evaluation.modalTitle', { id: selectedEvalForModal.operationId })}</h3>
                            </div>
                            <button onClick={() => setSelectedEvalForModal(null)} className="p-2 hover:bg-slate-800 rounded-full text-slate-400">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-8 space-y-8">
                            {/* Incidente Section */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <div className="w-1 h-3 bg-amber-500"></div> {t('evaluation.incidentDetailLabel')}
                                </h4>
                                <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700 text-slate-200 leading-relaxed font-mono text-sm">
                                    {selectedEvalForModal.generalObservations || t('evaluation.noObservations')}
                                </div>
                            </div>

                            {/* Recomendación Section */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <div className="w-1 h-3 bg-emerald-500"></div> {t('evaluation.strategicRecommendationVantak')}
                                </h4>
                                <div className="bg-emerald-500/5 p-5 rounded-2xl border border-emerald-500/20 text-emerald-100 leading-relaxed italic">
                                    {selectedEvalForModal.recommendations || t('evaluation.noRecommendation')}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="bg-slate-800/30 p-6 border-t border-slate-700 flex justify-end items-center text-xs text-slate-500">
                            <button
                                onClick={() => setSelectedEvalForModal(null)}
                                className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all font-bold uppercase tracking-wider"
                            >
                                {t('evaluation.understood')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
